import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  Check, 
  Bell,
  BellOff,
  RefreshCw,
  Lightbulb
} from 'lucide-react';

const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'critical': return <AlertTriangle className="w-5 h-5 text-red-500" />;
    case 'warning': return <AlertCircle className="w-5 h-5 text-amber-500" />;
    case 'info': return <Info className="w-5 h-5 text-blue-500" />;
    default: return <Bell className="w-5 h-5" />;
  }
};

const getSeverityBadge = (severity) => {
  switch (severity) {
    case 'critical': return <Badge className="bg-red-500 text-white">Critic</Badge>;
    case 'warning': return <Badge className="bg-amber-500 text-white">Avertizare</Badge>;
    case 'info': return <Badge className="bg-blue-500 text-white">Info</Badge>;
    default: return <Badge>Necunoscut</Badge>;
  }
};

const AlertCard = ({ alert, onAcknowledge }) => {
  const [acknowledging, setAcknowledging] = useState(false);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await onAcknowledge(alert.id);
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <Card 
      className={`border-l-4 ${
        alert.severity === 'critical' ? 'border-l-red-500 bg-red-50' :
        alert.severity === 'warning' ? 'border-l-amber-500 bg-amber-50' :
        'border-l-blue-500 bg-blue-50'
      }`}
      data-testid={`alert-card-${alert.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {getSeverityIcon(alert.severity)}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">{alert.employee_name}</span>
                {getSeverityBadge(alert.severity)}
              </div>
              <p className="text-sm text-foreground">{alert.message}</p>
              {alert.recommendation && (
                <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                  <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{alert.recommendation}</span>
                </div>
              )}
              {alert.metric_value !== null && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Valoare: {alert.metric_value?.toFixed(1)} (prag: {alert.threshold})
                </p>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAcknowledge}
            disabled={acknowledging}
            data-testid={`acknowledge-btn-${alert.id}`}
          >
            {acknowledging ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4 mr-1" />
                Confirmat
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AlertsSummary = ({ summary }) => {
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="p-4 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
          <p className="text-2xl font-bold text-red-600">{summary.critical}</p>
          <p className="text-sm text-muted-foreground">Critice</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
          <p className="text-2xl font-bold text-amber-600">{summary.warning}</p>
          <p className="text-sm text-muted-foreground">Avertizări</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <Info className="w-8 h-8 mx-auto mb-2 text-blue-500" />
          <p className="text-2xl font-bold text-blue-600">{summary.info}</p>
          <p className="text-sm text-muted-foreground">Informative</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <Bell className="w-8 h-8 mx-auto mb-2 text-slate-500" />
          <p className="text-2xl font-bold">{summary.total}</p>
          <p className="text-sm text-muted-foreground">Total</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default function PerformanceAlerts({ currentMonth }) {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ critical: 0, warning: 0, info: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const [alertsRes, summaryRes] = await Promise.all([
        api.get('/performance-alerts'),
        api.get('/performance-alerts/summary')
      ]);
      setAlerts(alertsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError(err.response?.data?.detail || 'Eroare la încărcarea alertelor');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/performance-alerts/${alertId}/acknowledge`);
      // Remove from list
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setSummary(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  const handleGenerateAlerts = async () => {
    setGenerating(true);
    try {
      const month = currentMonth || new Date().toISOString().slice(0, 7);
      await api.post(`/performance-alerts/generate?month=${month}`);
      await fetchAlerts();
    } catch (err) {
      console.error('Error generating alerts:', err);
      setError(err.response?.data?.detail || 'Eroare la generarea alertelor');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-alerts-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Alerte Performanță</h2>
          <p className="text-sm text-muted-foreground">Notificări automate pentru scoruri sub 50</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAlerts} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizează
          </Button>
          <Button 
            onClick={handleGenerateAlerts} 
            disabled={generating}
            size="sm"
            data-testid="generate-alerts-btn"
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Generează alerte
          </Button>
        </div>
      </div>

      <AlertsSummary summary={summary} />

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {alerts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BellOff className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">Nicio alertă activă</p>
            <p className="text-sm text-muted-foreground mt-1">
              Toate alertele au fost confirmate sau nu există probleme de performanță
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Critical alerts first */}
          {alerts.filter(a => a.severity === 'critical').map(alert => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
          {/* Then warnings */}
          {alerts.filter(a => a.severity === 'warning').map(alert => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
          {/* Then info */}
          {alerts.filter(a => a.severity === 'info').map(alert => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
        </div>
      )}
    </div>
  );
}
